import { useState } from "react";
import { useUploads } from "./UploadProvider";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ChevronDown, ChevronUp, X, Pause, Play, RotateCw, CheckCircle2, AlertCircle, Loader2, Upload as UploadIcon, WifiOff, LogIn, RefreshCw, Trash2, Minus,
} from "lucide-react";
import { cn } from "@/lib/utils";

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatSpeed(bps?: number): string {
  if (!bps || bps <= 0) return "";
  return `${formatBytes(bps)}/s`;
}

function formatEta(sec?: number): string {
  if (!sec || !isFinite(sec) || sec <= 0) return "";
  const s = Math.round(sec);
  if (s < 60) return `~${s}s left`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return r > 0 ? `~${m}m ${r}s left` : `~${m}m left`;
  const h = Math.floor(m / 60);
  const mr = m % 60;
  return mr > 0 ? `~${h}h ${mr}m left` : `~${h}h left`;
}

const CANCEL_CONFIRM_SIZE = 50 * 1024 * 1024;
const CANCEL_CONFIRM_PERCENT = 0.25;

export function GlobalUploadTray() {
  const { uploads, pause, resume, cancel, clearCompleted, pendingResume, resumeAll, discardAll } = useUploads();
  const [expanded, setExpanded] = useState(true);
  // P3: minimised "chip" mode — collapses tray to a small floating pill.
  const [minimised, setMinimised] = useState(false);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  const hasPendingResume = pendingResume.length > 0;
  if (uploads.length === 0 && !hasPendingResume) return null;

  const active = uploads.filter((u) => u.status === "uploading" || u.status === "queued");
  const paused = uploads.filter((u) => u.status === "paused");
  const failed = uploads.filter((u) => u.status === "failed");
  const completed = uploads.filter((u) => u.status === "completed");

  const totalBytes = uploads.reduce((sum, u) => sum + u.fileSize, 0);
  const uploadedBytes = uploads.reduce((sum, u) => sum + u.bytesUploaded, 0);
  const overallPercent = totalBytes ? (uploadedBytes / totalBytes) * 100 : 0;

  const confirmTarget = confirmCancelId ? uploads.find((u) => u.localUploadId === confirmCancelId) : null;

  const requestCancel = (u: typeof uploads[number]) => {
    const ratio = u.fileSize > 0 ? u.bytesUploaded / u.fileSize : 0;
    if (u.fileSize > CANCEL_CONFIRM_SIZE || ratio > CANCEL_CONFIRM_PERCENT) {
      setConfirmCancelId(u.localUploadId);
    } else {
      cancel(u.localUploadId);
    }
  };

  // Chip mode: small floating pill that expands back on click.
  if (minimised) {
    const summaryCount = active.length || paused.length || failed.length || completed.length;
    return (
      <button
        type="button"
        onClick={() => setMinimised(false)}
        className={cn(
          "fixed z-[100] bottom-4 right-4 flex items-center gap-2",
          "rounded-full border border-border bg-card shadow-2xl",
          "px-3 py-2 hover:bg-muted transition-colors",
        )}
        aria-label="Show upload tray"
      >
        {active.length > 0 ? (
          <Loader2 className="h-4 w-4 text-primary animate-spin" />
        ) : failed.length > 0 ? (
          <AlertCircle className="h-4 w-4 text-destructive" />
        ) : (
          <UploadIcon className="h-4 w-4 text-primary" />
        )}
        <span className="text-xs font-medium">
          {active.length > 0 ? `${Math.round(overallPercent)}%` : `${summaryCount}`}
        </span>
      </button>
    );
  }

  return (
    <>
      <div
        className={cn(
          "fixed z-[100] bottom-4 right-4 w-[360px] max-w-[calc(100vw-2rem)]",
          "rounded-lg border border-border bg-card shadow-2xl",
          "flex flex-col overflow-hidden",
        )}
        role="region"
        aria-label="Upload progress"
      >
        {/* Resume banner */}
        {hasPendingResume && (
          <div className="px-3 py-2 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-900 flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <div className="flex-1 min-w-0 text-xs">
              <div className="font-medium text-amber-900 dark:text-amber-100">
                {pendingResume.length} upload{pendingResume.length === 1 ? "" : "s"} waiting from your last session
              </div>
            </div>
            <Button size="sm" variant="default" className="h-7 text-xs" onClick={() => resumeAll()}>
              Resume all
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => discardAll()} aria-label="Discard pending uploads">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {/* Header */}
        {uploads.length > 0 && (
          <div className="flex items-center gap-1 px-3 py-2.5 bg-muted/50">
            <button
              type="button"
              className="flex items-center gap-2 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              <UploadIcon className="h-4 w-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {active.length > 0
                    ? `${active.length} upload${active.length === 1 ? "" : "s"} in progress`
                    : paused.length > 0
                      ? `${paused.length} upload${paused.length === 1 ? "" : "s"} paused`
                      : failed.length > 0
                        ? `${failed.length} upload${failed.length === 1 ? "" : "s"} failed`
                        : `${completed.length} upload${completed.length === 1 ? "" : "s"} complete`}
                </div>
                {active.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    {Math.round(overallPercent)}% — {formatBytes(uploadedBytes)} / {formatBytes(totalBytes)}
                  </div>
                )}
              </div>
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 shrink-0"
              onClick={() => setMinimised(true)}
              aria-label="Minimise upload tray"
              title="Minimise"
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {/* List */}
        {expanded && uploads.length > 0 && (
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-border">
            {uploads.map((u) => (
              <div key={u.localUploadId} className="p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <StatusIcon status={u.status} error={u.error} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" title={u.fileName}>
                      {u.fileName}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                      <span>{formatBytes(u.bytesUploaded)} / {formatBytes(u.fileSize)}</span>
                      {u.status === "uploading" && u.speedBps ? (
                        <>
                          <span>·</span>
                          <span>{formatSpeed(u.speedBps)}</span>
                        </>
                      ) : null}
                      {u.status === "uploading" && u.etaSec && isFinite(u.etaSec) ? (
                        <>
                          <span>·</span>
                          <span>{formatEta(u.etaSec)}</span>
                        </>
                      ) : null}
                      {u.status === "paused" && u.error ? (
                        <>
                          <span>·</span>
                          <span className="text-amber-600 dark:text-amber-400">{u.error}</span>
                          {u.attemptCount && u.maxAttempts && u.attemptCount > 0 ? (
                            <span className="text-muted-foreground">(retry {u.attemptCount}/{u.maxAttempts})</span>
                          ) : null}
                        </>
                      ) : null}
                      {u.status === "failed" && u.error ? (
                        <>
                          <span>·</span>
                          <span className="text-destructive truncate">{u.error}</span>
                          {u.attemptCount ? (
                            <span className="text-muted-foreground">(after {u.attemptCount} {u.attemptCount === 1 ? "try" : "tries"})</span>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex gap-0.5 shrink-0">
                    {u.status === "uploading" && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => pause(u.localUploadId)} aria-label="Pause">
                        <Pause className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {(u.status === "paused" || u.status === "failed") && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => resume(u.localUploadId)} aria-label="Resume">
                        {u.status === "failed" ? <RotateCw className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                    {u.status !== "completed" && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => requestCancel(u)} aria-label="Cancel">
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                <Progress
                  value={u.percent}
                  className={cn(
                    "h-1.5",
                    u.status === "failed" && "[&>div]:bg-destructive",
                    u.status === "paused" && "[&>div]:bg-muted-foreground",
                    u.status === "completed" && "[&>div]:bg-green-500",
                  )}
                />
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {expanded && completed.length > 0 && (
          <div className="px-3 py-2 border-t border-border bg-muted/30 flex justify-end">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => clearCompleted()}>
              Clear completed
            </Button>
          </div>
        )}
      </div>

      {/* Cancel confirmation dialog */}
      <AlertDialog open={!!confirmCancelId} onOpenChange={(o) => { if (!o) setConfirmCancelId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel upload?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget
                ? `Cancel upload of "${confirmTarget.fileName}"? Progress will be lost.`
                : "Cancel this upload? Progress will be lost."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep uploading</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (confirmCancelId) cancel(confirmCancelId);
                setConfirmCancelId(null);
              }}
            >
              Cancel upload
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function StatusIcon({ status, error }: { status: string; error?: string }) {
  if (status === "completed") return <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />;
  if (status === "failed") return <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />;
  if (status === "uploading") return <Loader2 className="h-4 w-4 text-primary mt-0.5 shrink-0 animate-spin" />;
  if (status === "paused") {
    if (error === "Network offline") return <WifiOff className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />;
    if (error === "Waiting for sign-in") return <LogIn className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />;
    return <Pause className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />;
  }
  return <UploadIcon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />;
}
